import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { useUpdateUser, useRoles, type User } from "../../services/userService";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Loader2 } from "lucide-react";

// Zod validation schema
const userEditSchema = z.object({
  name: z.string().min(1, "Name is required").max(150, "Name is too long"),
  email: z.string().email("Invalid email address"),
  password: z.string().min(8, "Password must be at least 8 characters").optional().or(z.literal("")),
  locale: z.string().optional(),
  roles: z.array(z.string()).optional(),
});

type UserEditFormData = z.infer<typeof userEditSchema>;

interface UserEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  user: User;
}

const UserEditModal: React.FC<UserEditModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  user,
}) => {
  const { t } = useTranslation();
  const updateUserMutation = useUpdateUser();
  const { data: roles } = useRoles();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<UserEditFormData>({
    resolver: zodResolver(userEditSchema),
    defaultValues: {
      name: user.name,
      email: user.email,
      password: "",
      locale: user.locale || "en",
      roles: user.roles?.map((role) => (typeof role === "string" ? role : role.name)) || [],
    },
  });

  // Reset form when user changes
  useEffect(() => {
    if (user) {
      reset({
        name: user.name,
        email: user.email,
        password: "",
        locale: user.locale || "en",
        roles: user.roles?.map((role) => (typeof role === "string" ? role : role.name)) || [],
      });
    }
  }, [user, reset]);

  const selectedRoles = watch("roles") || [];

  const toggleRole = (roleName: string) => {
    const currentRoles = selectedRoles;
    if (currentRoles.includes(roleName)) {
      setValue(
        "roles",
        currentRoles.filter((r) => r !== roleName)
      );
    } else {
      setValue("roles", [...currentRoles, roleName]);
    }
  };

  const onSubmit = async (data: UserEditFormData) => {
    try {
      // Remove password from data if it's empty
      const updateData: any = {
        name: data.name,
        email: data.email,
        locale: data.locale,
        roles: data.roles,
      };
      
      if (data.password && data.password.length > 0) {
        updateData.password = data.password;
      }

      await updateUserMutation.mutateAsync({
        id: user.id,
        data: updateData,
      });
      reset();
      onClose();
      onSuccess?.();
    } catch (error) {
      console.error("Failed to update user:", error);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t("admin.users.edit.title", "Edit User")}
          </DialogTitle>
          <DialogDescription>
            {t("admin.users.edit.description", "Update user information and roles")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">
              {t("admin.users.form.name", "Name")} *
            </Label>
            <Input
              id="name"
              {...register("name")}
              placeholder={t("admin.users.form.namePlaceholder", "Enter user name")}
            />
            {errors.name && (
              <p className="text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">
              {t("admin.users.form.email", "Email")} *
            </Label>
            <Input
              id="email"
              type="email"
              {...register("email")}
              placeholder={t("admin.users.form.emailPlaceholder", "Enter email address")}
            />
            {errors.email && (
              <p className="text-sm text-red-600">{errors.email.message}</p>
            )}
          </div>

          {/* Password */}
          <div className="space-y-2">
            <Label htmlFor="password">
              {t("admin.users.form.password", "Password")} ({t("admin.users.form.passwordOptional", "leave blank to keep current")})
            </Label>
            <Input
              id="password"
              type="password"
              {...register("password")}
              placeholder={t("admin.users.form.passwordPlaceholder", "Enter new password")}
            />
            {errors.password && (
              <p className="text-sm text-red-600">{errors.password.message}</p>
            )}
          </div>

          {/* Locale */}
          <div className="space-y-2">
            <Label htmlFor="locale">
              {t("admin.users.form.locale", "Locale")}
            </Label>
            <select
              id="locale"
              {...register("locale")}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="en">English</option>
              <option value="fr">Français</option>
            </select>
          </div>

          {/* Roles */}
          <div className="space-y-2">
            <Label>
              {t("admin.users.form.roles", "Roles")}
            </Label>
            <div className="space-y-2 max-h-40 overflow-y-auto border rounded-md p-3">
              {roles?.map((role) => (
                <div key={role.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`role-${role.id}`}
                    checked={selectedRoles.includes(role.name)}
                    onCheckedChange={() => toggleRole(role.name)}
                  />
                  <label
                    htmlFor={`role-${role.id}`}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    {role.name}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              {t("admin.users.form.cancel", "Cancel")}
            </Button>
            <Button
              type="submit"
              disabled={updateUserMutation.isPending}
            >
              {updateUserMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t("admin.users.form.updating", "Updating...")}
                </>
              ) : (
                t("admin.users.form.update", "Update User")
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UserEditModal;

